/**
 * The recordings manifest: one JSON object in the R2 bucket describing every
 * uploaded recording. Shared between the player (which reads it), the admin
 * page (which builds entries), and the Pages Function (which validates and
 * writes it). Keep this file dependency-free — it is imported from all three.
 *
 * All media references are *bucket keys*, not URLs; each consumer resolves
 * them against whatever origin it fetches the bucket through.
 */

export const MANIFEST_KEY = 'recordings.json'

/** Bucket keys for a recording all live under this prefix. */
export const recordingPrefix = (id: string) => `recordings/${id}/`

export interface ManifestVideoTier {
  key: string
  width: number
  height: number
  /** Codec string for `VideoDecoder.isConfigSupported`, e.g. "hvc1.1.6.L153.B0". */
  codec: string
  /**
   * True for tiers big enough that software decode drops frames; the player
   * then asks WebCodecs for `prefer-hardware`.
   */
  requireHardware: boolean
}

export interface ManifestAudioBank {
  /** Container extension the stems are stored as: flac | m4a | wav | mp3. */
  format: string
  /** Ordered stem keys; index matches `trackNames`. */
  keys: string[]
}

export interface ManifestRecording {
  /** Slug, also the bucket key prefix: recordings/<id>/… */
  id: string
  title: string
  /** Always 48000 in practice; the player pins its AudioContext to this. */
  sampleRate: number
  /** Exact sample count of the musical loop. The number that matters. */
  loopSamples: number
  /** Full media length in seconds (video may run past the musical loop). */
  mediaSeconds: number
  videoFps: number
  cols: number
  rows: number
  /** One name per tile, length must equal cols * rows. */
  trackNames: string[]
  /** Tempo percents with an audio bank, e.g. [100] or [25, 50, 75, 100]. */
  tempoPresets: number[]
  /** Keyed by String(tempoPercent). */
  audio: Record<string, ManifestAudioBank>
  video: {
    /** Preference-ordered tiers at full resolution. */
    full: ManifestVideoTier[]
    /** Optional smaller variants for small screens. */
    half?: ManifestVideoTier[]
  }
  posterKey: string | null
  createdAt?: string
}

export interface RecordingsManifest {
  version: 1
  recordings: ManifestRecording[]
}

export const ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,63}$/

/**
 * Validate an uploaded manifest entry. Returns an error message, or null when
 * the entry is usable. Deliberately structural, not exhaustive — the admin
 * page is authenticated, so this guards against mistakes, not attackers.
 */
export function validateRecording(value: unknown): string | null {
  const r = value as Partial<ManifestRecording> | null
  if (!r || typeof r !== 'object') return 'Recording must be an object.'
  if (typeof r.id !== 'string' || !ID_PATTERN.test(r.id)) {
    return 'id must be a lowercase slug (letters, digits, hyphens).'
  }
  if (typeof r.title !== 'string' || !r.title.trim()) return 'title is required.'
  if (r.sampleRate !== 48000) return 'sampleRate must be 48000.'
  if (!Number.isInteger(r.loopSamples) || (r.loopSamples as number) <= 0) {
    return 'loopSamples must be a positive integer.'
  }
  if (typeof r.mediaSeconds !== 'number' || r.mediaSeconds <= 0) {
    return 'mediaSeconds must be positive.'
  }
  if (typeof r.videoFps !== 'number' || r.videoFps <= 0 || r.videoFps > 240) {
    return 'videoFps out of range.'
  }
  if (!Number.isInteger(r.cols) || !Number.isInteger(r.rows) || (r.cols as number) < 1 || (r.rows as number) < 1) {
    return 'cols and rows must be positive integers.'
  }
  const tracks = (r.cols as number) * (r.rows as number)
  if (!Array.isArray(r.trackNames) || r.trackNames.length !== tracks) {
    return `trackNames must have exactly cols x rows (${tracks}) entries.`
  }
  if (!Array.isArray(r.tempoPresets) || r.tempoPresets.length === 0) {
    return 'tempoPresets must be a non-empty array.'
  }
  if (!r.audio || typeof r.audio !== 'object') return 'audio banks are required.'
  for (const tempo of r.tempoPresets) {
    const bank = (r.audio as Record<string, ManifestAudioBank>)[String(tempo)]
    if (!bank) return `tempo ${tempo}% is listed but has no audio bank.`
    if (!Array.isArray(bank.keys) || bank.keys.length !== tracks) {
      return `audio bank ${tempo}% must have exactly ${tracks} stems.`
    }
    if (!bank.keys.every((k) => typeof k === 'string' && k.startsWith(recordingPrefix(r.id!)))) {
      return `audio bank ${tempo}% has keys outside recordings/${r.id}/.`
    }
  }
  const tiers = [...(r.video?.full ?? []), ...(r.video?.half ?? [])]
  if (!r.video || !Array.isArray(r.video.full) || r.video.full.length === 0) {
    return 'at least one full video tier is required.'
  }
  for (const tier of tiers) {
    if (typeof tier.key !== 'string' || !tier.key.startsWith(recordingPrefix(r.id!))) {
      return `video tier key must live under recordings/${r.id}/.`
    }
    if (typeof tier.codec !== 'string' || !tier.codec) return 'video tier needs a codec string.'
    if (!Number.isInteger(tier.width) || !Number.isInteger(tier.height)) {
      return 'video tier needs integer dimensions.'
    }
  }
  if (r.posterKey !== null && r.posterKey !== undefined) {
    if (typeof r.posterKey !== 'string' || !r.posterKey.startsWith(recordingPrefix(r.id!))) {
      return `posterKey must live under recordings/${r.id}/.`
    }
  }
  return null
}
